// ─────────────────────────────────────────────────────────────────
// Glass-i — Customer Master page-level constants (Stage 6A.2).
//
// Wraps the shared India-GST constants and adds page-only display
// metadata (status colours, registration-type tag colours, default
// form values). Keeps imports inside the page directory short.
// ─────────────────────────────────────────────────────────────────

export {
  GST_STATE_CODES,
  GST_REGISTRATION_TYPES,
  REGISTRATION_TYPES_REQUIRING_GSTIN,
  GST_VERIFICATION_STATUSES,
  SEZ_TYPES,
  CUSTOMER_STATUSES,
  CUSTOMER_CATEGORY_SUGGESTIONS,
  STATE_SELECT_OPTIONS,
  GSTIN_REGEX,
  PAN_REGEX,
  isValidGstin,
  isValidPan,
  stateNameFromCode,
  panFromGstin,
  stateCodeFromGstin,
  registrationTypeRequiresGstin
} from '../../../../constants/indiaGst';

export const REGISTRATION_TYPE_TAG_COLOUR = Object.freeze({
  registered_regular:     'blue',
  registered_composition: 'cyan',
  unregistered:           'default',
  consumer:               'default',
  sez:                    'gold',
  export:                 'magenta',
  deemed_export:          'purple'
});

export const STATUS_TAG_COLOUR = Object.freeze({
  active:   'success',
  inactive: 'default',
  blocked:  'error'
});

export const VERIFICATION_TAG_COLOUR = Object.freeze({
  verified:        'success',
  not_verified:    'default',
  failed:          'error',
  manual_override: 'warning'
});

// Stage 6A.2K — Price Group dropdown options.
// TODO: Bind from Pricing Master / Price Group API in a future stage.
// The options below are static placeholders so the Customer Master can
// already capture the field today without waiting for the Pricing
// module. Backend stores the value as a free-form VARCHAR(60).
// PRICE_GROUP_OPTIONS (standard/dealer/project/retail/special) was removed in
// Stage 6PL-1. Those five labels were invented here and matched nothing: a
// price list reaches a customer through an ASSIGNMENT, so a label that no card
// targeted silently priced nothing. The Customer Master now picks a real price
// list instead. `customers.price_group` itself is untouched — existing values
// are preserved and PRICE_GROUP-scope assignments still resolve against them.

// Stage 6A.2K — strict Indian mobile rule shared by the drawer's
// validators. Exactly 10 digits, first digit must be 6 / 7 / 8 / 9.
// `+91` and country-code prefixes are NOT accepted in Customer Master.
export const MOBILE_INDIA_REGEX = /^[6-9][0-9]{9}$/;

// Stage 6A.2D-Popup-Light — GST Law 2026 dropdown options.
export const MSME_CATEGORY_OPTIONS = Object.freeze([
  { value: 'micro',  label: 'Micro' },
  { value: 'small',  label: 'Small' },
  { value: 'medium', label: 'Medium' }
]);

export const GST_FILING_FREQUENCY_OPTIONS = Object.freeze([
  { value: 'monthly', label: 'Monthly' },
  { value: 'qrmp',    label: 'QRMP (Quarterly)' }
]);

export const TDS_SECTION_OPTIONS = Object.freeze([
  { value: '51',   label: 'Sec 51 GST (Govt deductee)' },
  { value: '194C', label: 'Sec 194C — Contractor' },
  { value: '194I', label: 'Sec 194I — Rent' },
  { value: '194J', label: 'Sec 194J — Professional fees' },
  { value: '194Q', label: 'Sec 194Q — Purchase of goods (>₹50L)' },
  { value: '194R', label: 'Sec 194R — Benefits / perquisites' },
  { value: '194S', label: 'Sec 194S — Crypto / VDA' },
  { value: '194T', label: 'Sec 194T — Partnership-firm payouts' }
]);

export const DEFAULT_CUSTOMER_FORM = Object.freeze({
  // Stage 6A.2E.3 — customerType field removed.
  gstRegistrationType: 'unregistered',
  status: 'active',
  isActive: true,
  isSez: false,
  isCompositionDealer: false,
  isReverseChargeApplicable: false,
  sameAsBilling: true,
  unloadingRequired: false,
  specialPackingRequired: false,
  billingCountry: 'India',
  shippingCountry: 'India',
  creditLimit: 0,
  creditDays: 0,
  openingBalance: 0
});
